import type { StaticVideoBlock } from '../types';

type StaticVideoProps = {
  video: StaticVideoBlock;
  'data-testid'?: string;
};

/**
 * Native video player sourced from static page JSON.
 *
 * Uses the browser's accessible controls and does not preload the file until
 * the reader starts playback. `width` and `height` set the aspect ratio; the
 * player scales to the content width.
 *
 * Captions render as a `<track>` when the block supplies them.
 *
 * @param video - Source, intrinsic size, optional poster, accessible label and captions from a `{ type: 'video' }` block
 */
function StaticVideo({ video, 'data-testid': dataTestId = 'static-video' }: StaticVideoProps) {
  return (
    // Captions are content-driven: the track renders whenever the JSON supplies them.
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      src={video.src}
      poster={video.poster}
      width={video.width}
      height={video.height}
      aria-label={video.ariaLabel}
      controls
      playsInline
      preload="none"
      className="h-auto w-full rounded-md bg-black"
      data-testid={dataTestId}
    >
      {video.captions ? <track kind="captions" src={video.captions.src} srcLang={video.captions.srcLang} label={video.captions.label} /> : null}
    </video>
  );
}

export { StaticVideo, type StaticVideoProps };
